"use client"

import { useEffect, useState } from 'react'
import { Search, User, UserX } from 'lucide-react'
import { useManagerSuggestionStore } from '../../stores/managerSuggestionStore'
import { getFullName } from '../../utils/orgHierarchyUtils'
import type { PotentialManager } from '../../types/orgHierarchy'
import { Spinner } from '@t-one-internal/design-system'
import apiClient from '../../api/apiClient'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@t-one-internal/design-system/lib/components/ui/popover'

interface SmartManagerDropdownProps {
    scopeInstanceId?: string
    employeeId?: string
    onSelectManager: (manager: PotentialManager | null) => void
    selectedManagerId?: string | null
    className?: string
    allowClear?: boolean
}

export const SmartManagerDropdown = ({
    scopeInstanceId,
    employeeId,
    onSelectManager,
    selectedManagerId,
    className = '',
    allowClear = false,
}: SmartManagerDropdownProps) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    // Fallback: if selectedManagerId isn't found in the groups list, resolve from API
    const [resolvedSelectedManager, setResolvedSelectedManager] = useState<PotentialManager | null>(null)

    const { groupedManagers, suggestedManager, isLoading, fetchPotentialManagers } =
        useManagerSuggestionStore()

    useEffect(() => {
        // Fetch potential managers - works with or without scopeInstanceId
        // If scopeInstanceId is provided, managers will be grouped by unit proximity
        // If not provided, all employees will be returned as "All Employees"
        fetchPotentialManagers({
            scope_instance_id: scopeInstanceId || '',
            employee_id: employeeId,
            grouped: true,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeInstanceId, employeeId])

    const handleSelectManager = (manager: PotentialManager | null) => {
        onSelectManager(manager)
        setIsOpen(false)
        setSearchQuery('')
    }

    const handleClearSelection = () => {
        onSelectManager(null)
        setIsOpen(false)
        setSearchQuery('')
    }

    const filterManagers = (managers: PotentialManager[]) => {
        if (!searchQuery.trim()) return managers
        const query = searchQuery.toLowerCase()
        return managers.filter(
            (manager) =>
                manager.fname?.toLowerCase().includes(query) ||
                manager.lname?.toLowerCase().includes(query) ||
                manager.email?.toLowerCase().includes(query) ||
                manager.job_title?.toLowerCase().includes(query),
        )
    }

    const selectedManagerFromList = groupedManagers?.groups
        .flatMap((group) => group.managers)
        .find((m) => m.user_uuid === selectedManagerId)

    // Fallback: resolve selected manager from API if not found in the groups list
    useEffect(() => {
        if (selectedManagerId && !selectedManagerFromList && !isLoading) {
            let cancelled = false
            const resolveManager = async () => {
                try {
                    const { data } = await apiClient.get<{ user?: any }>(`/admin/users/${selectedManagerId}`)
                    if (cancelled) return
                    const user = (data as any).user || data
                    setResolvedSelectedManager({
                        user_id: 0,
                        user_uuid: selectedManagerId,
                        fname: user.first_name || user.fname || '',
                        lname: user.last_name || user.lname || '',
                        email: user.email || '',
                        job_title: user.job_title || null,
                        scope_name: null,
                        priority_group: 4,
                        is_head: false,
                    })
                } catch (err) {
                    console.error('Failed to resolve selected manager:', err)
                    if (!cancelled) setResolvedSelectedManager(null)
                }
            }
            resolveManager()
            return () => { cancelled = true }
        } else if (!selectedManagerId) {
            setResolvedSelectedManager(null)
        }
    }, [selectedManagerId, selectedManagerFromList, isLoading])

    const selectedManager = selectedManagerFromList || resolvedSelectedManager

    return (
        <div className={className}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="w-full h-9 px-3 text-left bg-white border border-slate-200 rounded-md hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-colors flex items-center justify-between"
                    >
                        {selectedManager ? (
                            <div className="flex items-center gap-2 overflow-hidden min-w-0">
                                <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                    <User className="w-3 h-3 text-indigo-600" />
                                </div>
                                <span className="text-sm text-slate-700 truncate min-w-0">
                                    {getFullName(selectedManager.fname, selectedManager.lname)}
                                </span>
                            </div>
                        ) : (
                            <span className="text-sm text-slate-500">Select manager...</span>
                        )}
                        <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" />
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <div className="flex flex-col max-h-72 overflow-hidden bg-white rounded-lg shadow-lg border border-slate-200">
                        {/* Search */}
                        <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-slate-400"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Not Assigned Option */}
                        {allowClear && !searchQuery && (
                            <button
                                type="button"
                                onClick={handleClearSelection}
                                className={`w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 border-b border-slate-100 ${!selectedManagerId ? 'bg-slate-50' : ''}`}
                            >
                                <UserX className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600">Not Assigned</span>
                            </button>
                        )}

                        {/* Suggested Manager */}
                        {suggestedManager && !searchQuery && (
                            <div className="border-b border-slate-100">
                                <div className="px-3 py-1.5 text-[10px] font-semibold text-indigo-500 uppercase tracking-wider bg-indigo-50/50">
                                    Suggested
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleSelectManager(suggestedManager)}
                                    className="w-full px-3 py-2 text-left hover:bg-indigo-50/50 transition-colors flex items-center gap-2"
                                >
                                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <User className="w-3.5 h-3.5 text-indigo-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm font-medium text-slate-800 truncate block">
                                            {getFullName(suggestedManager.fname, suggestedManager.lname)}
                                        </span>
                                        <span className="text-xs text-slate-500 truncate block">
                                            {suggestedManager.job_title || 'No title'}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Manager List */}
                        <div className="overflow-y-auto flex-1">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Spinner size="sm" />
                                </div>
                            ) : groupedManagers ? (
                                <div>
                                    {groupedManagers.groups.map((group) => {
                                        const filteredManagers = filterManagers(group.managers)
                                        if (filteredManagers.length === 0) return null

                                        return (
                                            <div key={group.priority}>
                                                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 sticky top-0">
                                                    {group.label}
                                                </div>
                                                <div>
                                                    {filteredManagers.map((manager) => (
                                                        <button
                                                            key={manager.user_uuid}
                                                            type="button"
                                                            onClick={() => handleSelectManager(manager)}
                                                            className={`w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 ${selectedManagerId === manager.user_uuid ? 'bg-indigo-50' : ''}`}
                                                        >
                                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                                <User className="w-3.5 h-3.5 text-slate-400" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span
                                                                        className={`text-sm truncate ${selectedManagerId === manager.user_uuid ? 'text-indigo-900 font-medium' : 'text-slate-700'}`}
                                                                    >
                                                                        {getFullName(manager.fname, manager.lname)}
                                                                    </span>
                                                                    {manager.is_head && (
                                                                        <span className="text-[9px] font-medium text-amber-600 bg-amber-50 px-1 rounded">
                                                                            Head
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="text-xs text-slate-400 truncate block">
                                                                    {manager.job_title || 'No title'}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {groupedManagers.groups.every(
                                        (group) => filterManagers(group.managers).length === 0,
                                    ) && (
                                        <div className="px-3 py-6 text-center">
                                            <p className="text-sm text-slate-500">No managers found</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="px-3 py-6 text-center text-slate-400 text-sm">
                                    No managers available
                                </div>
                            )}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
